import { useCallback, type Dispatch, type MutableRefObject, type SetStateAction } from "react";
import type { ProviderKind } from "@cmmd-forge/contracts";

import type { ComposerTrigger } from "../../composer-logic";
import type { ComposerCommandItem } from "./ComposerCommandMenu";
import { extendReplacementRangeForTrailingSpace } from "./ChatComposer.logic";

interface ComposerSnapshot {
  readonly value: string;
}

type ApplyPromptReplacement = (
  start: number,
  end: number,
  replacement: string,
  options: { readonly expectedText: string },
) => boolean;

export function useComposerMenuItemSelection(input: {
  readonly selectLockRef: MutableRefObject<boolean>;
  readonly resolveActiveTrigger: () => {
    readonly snapshot: ComposerSnapshot;
    readonly trigger: ComposerTrigger | null;
  };
  readonly applyPromptReplacement: ApplyPromptReplacement;
  readonly setHighlightedItemId: Dispatch<SetStateAction<string | null>>;
  readonly onOpenUsageLimits: () => boolean;
  readonly handleInteractionModeChange: (mode: "default" | "plan") => void;
  readonly onProviderModelSelect: (provider: ProviderKind, model: string) => void;
}): (item: ComposerCommandItem) => void {
  return useCallback(
    (item) => {
      if (input.selectLockRef.current) return;
      input.selectLockRef.current = true;
      window.requestAnimationFrame(() => {
        input.selectLockRef.current = false;
      });
      const { snapshot, trigger } = input.resolveActiveTrigger();
      if (!trigger) return;
      if (item.type === "path") {
        applyComposerReplacement(input, snapshot, trigger, `@${item.path} `);
        return;
      }
      if (item.type === "slash-command") {
        selectBuiltInSlashCommand(input, item.command, snapshot, trigger);
        return;
      }
      if (item.type === "provider-slash-command") {
        applyComposerReplacement(input, snapshot, trigger, `/${item.command.name} `);
        return;
      }
      if (item.type === "skill") {
        applyComposerReplacement(input, snapshot, trigger, `$${item.skill.name} `);
        return;
      }
      input.onProviderModelSelect(item.provider, item.model);
      clearComposerReplacement(input, snapshot, trigger);
    },
    [input],
  );
}

function selectBuiltInSlashCommand(
  input: Parameters<typeof useComposerMenuItemSelection>[0],
  command: "model" | "plan" | "default" | "usage-limits",
  snapshot: ComposerSnapshot,
  trigger: ComposerTrigger,
): void {
  if (command === "model") {
    applyComposerReplacement(input, snapshot, trigger, "/model ");
    return;
  }
  if (command === "usage-limits") {
    if (input.onOpenUsageLimits()) clearComposerReplacement(input, snapshot, trigger);
    return;
  }
  input.handleInteractionModeChange(command === "plan" ? "plan" : "default");
  clearComposerReplacement(input, snapshot, trigger);
}

function applyComposerReplacement(
  input: Parameters<typeof useComposerMenuItemSelection>[0],
  snapshot: ComposerSnapshot,
  trigger: ComposerTrigger,
  replacement: string,
): void {
  const end = extendReplacementRangeForTrailingSpace(snapshot.value, trigger.rangeEnd, replacement);
  if (
    input.applyPromptReplacement(trigger.rangeStart, end, replacement, {
      expectedText: snapshot.value.slice(trigger.rangeStart, end),
    })
  ) {
    input.setHighlightedItemId(null);
  }
}

function clearComposerReplacement(
  input: Parameters<typeof useComposerMenuItemSelection>[0],
  snapshot: ComposerSnapshot,
  trigger: ComposerTrigger,
): void {
  if (
    input.applyPromptReplacement(trigger.rangeStart, trigger.rangeEnd, "", {
      expectedText: snapshot.value.slice(trigger.rangeStart, trigger.rangeEnd),
    })
  ) {
    input.setHighlightedItemId(null);
  }
}
